/**
 * WI-B-04 / WI-B-07 — the AuthGate controller + the dev stub AuthKit provider.
 *
 * AuthGate owns the login state machine and decides what mounts:
 *   unauth        → A1 launch
 *   redirecting   → A2 (handing off to hosted AuthKit)
 *   callback      → A4 (code→token→scope, via resolveCallback)
 *   error(kind)   → A5
 *   no-projects   → A6
 *   authed        → renders children (the App shell) — and ONLY then, so the
 *                   read client is never built under the baked pilot bearer
 *                   (main.tsx:70-84 cross-tenant leak, 2026-07-30). establishing
 *                   the session BEFORE children mount is the fix seam.
 *
 * WI-B-07: with VITE_MARK_AUTH_STUB=1 (dev only) the "hosted AuthKit" round-trip
 * is replaced by a local fixture magic-link — Sign in resolves against a mock
 * directory so Red can click the whole flow with `npm run dev`, no live WorkOS.
 * With the flag off, onSignIn redirects to the real authorize_url (live leg,
 * §S8) — not exercised here.
 */

import { useCallback, useEffect, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import {
  AuthLaunch,
  AuthRedirecting,
  AuthCallback,
  AuthError,
  AuthNoProjects,
} from "./AuthScreens";
import { SessionStaleBanner } from "./session-banner";
import {
  resolveCallback,
  type AuthErrorKind,
  type CodeExchange,
  type ScopeFetch,
} from "../auth/workos-callback";
import type { EstablishedSession, MembershipCheck } from "../auth/workos-session";
import { installSession, clearSession } from "../auth/session-store";
import { clearSessionStale, isSessionStale, onSessionStaleChange } from "../auth/session-stale";

type Phase =
  | { name: "unauth"; reauth: boolean }
  | { name: "redirecting" }
  | { name: "callback" }
  | { name: "error"; kind: AuthErrorKind }
  | { name: "no-projects"; email: string };

export interface AuthDriver {
  /** Begin login. In stub mode this resolves a fixture code locally; in live
   * mode it redirects to the hosted authorize_url (and never returns here). */
  beginLogin(): Promise<{ code: string } | { error: string }>;
  exchange: CodeExchange;
  fetchScope: ScopeFetch;
  /** Bind the verified token's org/role to a real directory membership — a
   * cross-org or role-escalated token is rejected (FIX-B1). Optional so a test
   * driver can omit it; the stub + live drivers supply it. */
  checkMembership?: MembershipCheck;
  /** Queued-capture count, for the A5 reassurance copy. */
  queuedCount(): Promise<number>;
}

/**
 * The gate. Renders children only once a session is established and installed.
 */
export function AuthGate({
  driver,
  children,
  initialReauth = false,
  establishedSession = null,
}: {
  driver: AuthDriver;
  children: ComponentChildren;
  initialReauth?: boolean;
  /**
   * Cookie mode (Option 1): the session is already established server-side and
   * proven by CookieAuthedApp's /auth/session probe. Passing it here seeds the
   * gate as authed so it mounts the shell (with the stale-banner chrome) instead
   * of re-running its own resolveCallback login machine — which cookie mode never
   * drives, so the gate would otherwise paint A1 launch OVER the authed children.
   */
  establishedSession?: EstablishedSession | null;
}) {
  const [phase, setPhase] = useState<Phase>({ name: "unauth", reauth: initialReauth });
  const [session, setSession] = useState<EstablishedSession | null>(establishedSession);
  const [queued, setQueued] = useState(0);

  const runLogin = useCallback(async () => {
    setPhase({ name: "redirecting" });
    const started = await driver.beginLogin();
    setPhase({ name: "callback" });
    const params = "code" in started ? { code: started.code } : { error: started.error };
    const result = await resolveCallback(params, driver.exchange, driver.fetchScope, driver.checkMembership);

    if (result.kind === "session") {
      installSession(result.session);
      // Re-auth resumes held captures: clearing the stale flag lets the outbox
      // dispatcher drain the queue it was holding (PIN-B-4). No-op on first login.
      clearSessionStale();
      setSession(result.session);
      return; // children mount
    }
    if (result.kind === "no-projects") {
      // Signed in, but no project — A6, never a projectless shell.
      setPhase({ name: "no-projects", email: result.current_user.user_id });
      return;
    }
    // error → A5, with the queued count for the reassurance block.
    setQueued(await driver.queuedCount().catch(() => 0));
    setPhase({ name: "error", kind: result.error });
  }, [driver]);

  if (session) {
    return (
      <AuthedShell driver={driver} onReauth={() => void runLogin()}>
        {children}
      </AuthedShell>
    );
  }

  switch (phase.name) {
    case "redirecting":
      return <AuthRedirecting />;
    case "callback":
      return <AuthCallback />;
    case "error":
      return (
        <AuthError
          kind={phase.kind}
          queuedCount={queued}
          onRetry={() => void runLogin()}
          onKeepCapturing={() => setPhase({ name: "unauth", reauth: true })}
        />
      );
    case "no-projects":
      return (
        <AuthNoProjects
          email={phase.email}
          onCheckAgain={() => void runLogin()}
          onSignOut={() => {
            clearSession();
            setSession(null);
            setPhase({ name: "unauth", reauth: false });
          }}
        />
      );
    case "unauth":
    default:
      return <AuthLaunch onSignIn={() => void runLogin()} reauth={phase.reauth} />;
  }
}

/**
 * The authed frame: the shell (children) with the A7 session-stale banner pinned
 * above it. The banner only renders when the session has gone stale (a bare 401
 * on the send path marked it — WI-B-05); tapping it re-runs login WITHOUT
 * unmounting the shell, so capture keeps working the whole time (PIN-B-4). This
 * is the wiring Codex blocker 4 flagged: the banner + resume path exist but were
 * never mounted.
 */
function AuthedShell({
  driver,
  onReauth,
  children,
}: {
  driver: AuthDriver;
  onReauth: () => void;
  children: ComponentChildren;
}) {
  // The banner self-gates on the stale signal (renders null when healthy), so it
  // is always mounted above the shell; we only feed it the queued count for the
  // reassurance copy, refreshed whenever the session goes stale.
  const [stale, setStale] = useState<boolean>(isSessionStale());
  const [queued, setQueued] = useState(0);
  useEffect(() => onSessionStaleChange(setStale), []);
  useEffect(() => {
    if (!stale) return;
    let live = true;
    void driver.queuedCount().then((n) => {
      if (live) setQueued(n);
    }).catch(() => {});
    return () => {
      live = false;
    };
  }, [stale, driver]);
  return (
    <>
      <div style={{ padding: "8px 12px 0" }}>
        <SessionStaleBanner stale={stale} queuedCount={queued} onSignIn={onReauth} />
      </div>
      {children}
    </>
  );
}
